import React, { FC, memo, useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  object,
  string,
  ref
} from 'yup';

import { theme } from 'theme';
import { gatewayService } from 'services';
import {
  TextInput,
  Text,
  PrimaryButton
} from 'components/atoms';
import {
  Background,
  Footer
} from 'components/molecules';
import { PageProps } from 'components/AppRouter';
import {
  isFulfiller
} from 'config/privileges';
import {
  getNetworkErrors
} from 'utils/general';
import {
  NetworkError
} from 'types/Error';
import { Form } from 'types/UI';
import { useValidation } from 'components/hooks';

import {
  Wrapper,
  StyledCard,
  StyledFormWrapper,
  FormLine,
  StyledHeader,
  StyledLink,
  HR,
} from './ChangeDefaultPassword.styles';

interface State {
  request: {
    loading: boolean;
    success: boolean;
    error: NetworkError | null;
  };
  form: Form;
}

const initialState: State = {
  request: {
    loading: false,
    success: false,
    error: null
  },
  form: {}
};

const changeDefaultPasswordSchema = object({
  oldPassword: string()
    .label('Current password')
    .required(),
  password: string()
    .label('New password')
    .notOneOf([ref('oldPassword')], 'New password must be different')
    .required(),
  confirmPassword: string()
    .label('Confirm password')
    .oneOf([ref('password')], 'Passwords must match')
    .required()
});

const ChangeDefaultPassword: FC<PageProps> = props => {
  const {
    userData
  } = props;

  const navigate = useNavigate();

  const [state, setState] = useState<State>(initialState);
  const { errors, validate } = useValidation(changeDefaultPasswordSchema);

  const request = useCallback((payload: any) => {
    if (state.request.loading) {
      return;
    }

    setState(prevState => ({
      ...prevState,
      request: {
        ...prevState.request,
        loading: true
      }
    }));

    const serviceMethod = isFulfiller(userData.user!) ? gatewayService.updateFulfiller : gatewayService.updateUser;

    serviceMethod(
      userData.user!.clientId,
      userData.user!._id,
      payload
    )
      .then(() => {
        setState(prevState => ({
          ...prevState,
          request: {
            loading: false,
            success: true,
            error: null
          },
        }));
      })
      .catch((err) => {
        setState(prevState => ({
          ...prevState,
          request: {
            ...prevState.request,
            loading: false,
            success: false,
            error: getNetworkErrors([err])[0]
          }
        }));
      });
  }, [
    userData.user,
    state.request.loading
  ]);

  const handleOnChange = useCallback((e: any, key: string) => {
    const value = e.target.value;

    const newState = {
      ...state,
      form: {
        ...state.form,
        [key]: value
      }
    };

    setState(newState);
  }, [state]);

  const handleSubmit = useCallback((e: any) => {
    e.preventDefault();

    validate({
      form: state.form,
      all: true
    })
      .then(() => {
        const { oldPassword, password } = state.form;

        request({
          oldPassword,
          newPassword: password
        });
      });
  }, [
    state,
    request,
    validate
  ]);

  useEffect(() => {
    if (!userData.user) {
      navigate({ pathname: '/login' });
    }
  }, [
    userData,
    navigate
  ]);

  let submitError = null;

  if (state.request.error) {
    submitError = (
      <Text
        isError
        value={state.request.error.message}
      ></Text>
    );
  }

  return (
    <>
      <Background />
      <Wrapper>
        <StyledCard boxShadow>
          <StyledFormWrapper onSubmit={handleSubmit}>
            <FormLine
              center
              marginBottom
            >
              <StyledHeader>Change default password</StyledHeader>
            </FormLine>

            <FormLine marginBottom>
              <HR />
            </FormLine>
            <FormLine marginBottom />

            {!state.request.success && (
              <FormLine column>
                <FormLine marginBottom>
                  <TextInput
                    name={'current-password'}
                    width={'100%'}
                    label="Current password"
                    type="password"
                    value={state.form.oldPassword as string}
                    error={errors.oldPassword}
                    onChange={(e) => handleOnChange(e, 'oldPassword')}
                    onBlur={() => validate({
                      form: state.form,
                      field: 'oldPassword'
                    })}
                  />
                </FormLine>
                <FormLine marginBottom>
                  <TextInput
                    name={'password'}
                    width={'100%'}
                    label="New password"
                    type="password"
                    value={state.form.password as string}
                    error={errors.password}
                    onChange={(e) => handleOnChange(e, 'password')}
                    onBlur={() => validate({
                      form: state.form,
                      field: 'password'
                    })}
                  />
                </FormLine>
                <FormLine marginBottom>
                  <TextInput
                    name={'confirm-password'}
                    width={'100%'}
                    label="Confirm password"
                    type="password"
                    value={state.form.confirmPassword as string}
                    error={errors.confirmPassword}
                    onChange={(e) => handleOnChange(e, 'confirmPassword')}
                    onBlur={() => validate({
                      form: state.form,
                      field: 'confirmPassword'
                    })}
                  />
                </FormLine>

                <FormLine marginBottom />

                <FormLine right>
                  <PrimaryButton
                    width={'100%'}
                    loading={state.request.loading}
                    spinnerColor={theme.colors.coreSecondary}
                    disabled={state.request.loading}
                    type="submit"
                  >
                    Submit
                  </PrimaryButton>
                </FormLine>
              </FormLine>
            )}

            {submitError && (
              <FormLine
                center
                marginBottom
              >
                {submitError}
              </FormLine>
            )}

            {state.request.success && (
              <FormLine
                center
                marginBottom
              >
                <p>Password successfully updated. Please login <StyledLink to="/login">here</StyledLink></p>
              </FormLine>
            )}
          </StyledFormWrapper>
        </StyledCard>
        <FormLine
          center
          marginBottom
        >
          <Footer />
        </FormLine>
      </Wrapper>
    </>
  );
};

export default memo(ChangeDefaultPassword);

